/** Sources reviewed 2026-09-07. Examples are not population norms or personal targets. */
export const CHART_SOURCES = {
  race: { title: 'Joe Friel · The Right Numbers for Race Day', url: 'https://www.trainingpeaks.com/learn/articles/applying-the-numbers-part-3-training-stress-balance/' },
  tsb: { title: 'TrainingPeaks · Performance Insight Categories (2026)', url: 'https://help.trainingpeaks.com/hc/en-us/articles/41077784209165-Athlete-Home-Performance-Insights' },
  ctl: { title: 'TrainingPeaks · Joe Dombrowski, Giro 2018', url: 'https://www.trainingpeaks.com/blog/joe-dombrowski-giro-ditalia-power-files/' },
  volume: { title: 'TrainingPeaks · Ryan Mullen, 2017', url: 'https://www.trainingpeaks.com/blog/a-detailed-analysis-of-cannondale-drapac-rider-ryan-mullens-2017-classics-campaign/' },
  tss: { title: 'TrainingPeaks · Training Stress Scores Explained', url: 'https://help.trainingpeaks.com/hc/en-us/articles/204071944-Training-Stress-Scores-TSS-Explained' },
  power: { title: 'TrainingPeaks · Kate Courtney, Worlds 2018', url: 'https://www.trainingpeaks.com/blog/see-kate-courtneys-xco-world-championship-winning-power-file/' },
  zones: { title: 'Esteve-Lanao et al. · 12 runners (2007)', url: 'https://pubmed.ncbi.nlm.nih.gov/17685689/' },
  wprime: { title: 'Team-Pursuit Cyclists · 3 teams (2022)', url: 'https://pubmed.ncbi.nlm.nih.gov/36068071/' },
  decoupling: { title: 'TrainingPeaks · Aerobic Decoupling', url: 'https://help.trainingpeaks.com/hc/en-us/articles/204071724-Aerobic-Decoupling-Pw-Hr-and-Pa-HR-and-Efficiency-Factor-EF' },
  intensity: { title: 'TrainingPeaks · NP, IF and TSS', url: 'https://www.trainingpeaks.com/learn/articles/normalized-power-intensity-factor-training-stress/' },
} as const;

export function chartReferenceKey(type: string, setting: Record<string, unknown> | null = null): string {
  const config = setting ?? {};
  if (type === 'periodic_chart' || type === 'fitness_summery_chart') {
    const summary = config.workout_summery as Record<string, unknown> | undefined;
    const metric = String(type === 'periodic_chart' ? summary?.workout_summery_types ?? 'tss_actual' : config.fitness_type ?? 'distance');
    if (metric.includes('tss')) return 'tss';
    if (metric.startsWith('distance') || metric === 'total_time' || metric.includes('duration')) return 'volume';
    if (metric.includes('calor')) return 'nutrition';
    if (metric === 'if_actual' || metric === 'if' || metric === 'intensity_factor') return 'intensity';
    if (metric === 'work') return 'work';
    if (metric === 'total_ascent' || metric === 'total_descent') return 'elevation';
    return 'general';
  }
  const keys: Record<string, string> = {
    pmc_report: 'pmc', fitness_summery_report: 'volume', nutrition_chart: 'nutrition',
    power_profile: 'power', peak_records: 'power', power_duration: 'power', time_in_zone: 'zones',
    metrics_chart: 'body', estimated_ftp: 'ftp', w_prime: 'wprime', decoupling: 'decoupling',
    readiness: 'readiness', durability: 'durability', heatmap: 'tss', scatter: 'scatter', formula: 'formula', compliance: 'compliance',
  };
  return keys[type] ?? 'general';
}

export const REFERENCE_SOURCES: Record<string, (keyof typeof CHART_SOURCES)[]> = {
  pmc: ['tsb', 'race', 'ctl'], volume: ['volume'], tss: ['tss'], power: ['power'],
  zones: ['zones'], wprime: ['wprime'], decoupling: ['decoupling'],
  intensity: ['intensity'],
};

// Continuous raw TSB (points), NOT Intervals.icu's percentage Form scale.
export const TSB_ZONES = [
  { key: 'overload', min: -Infinity, max: -30, range: 'TSB ≤ −30', color: '#f19b7c' },
  { key: 'optimal', min: -30, max: -10, range: '−30 < TSB < −10', color: '#48c9a0' },
  { key: 'maintaining', min: -10, max: 5, range: '−10 ≤ TSB < 5', color: '#a7b5ca' },
  { key: 'fresh', min: 5, max: 30, range: '5 ≤ TSB < 30', color: '#72b7f7' },
  { key: 'recharging', min: 30, max: Infinity, range: 'TSB ≥ 30', color: '#c2a2ed' },
] as const;

// Race-day guidance overlaps the Fresh category; it is not a new universal TSB class.
export const TSB_RACE_RANGE = { key: 'race', min: 15, max: 25, range: '+15 … +25', color: '#a9bbff' } as const;
export const TSB_REFERENCE_BANDS = [...TSB_ZONES.slice(0, 2), TSB_RACE_RANGE];

export function tsbZoneKey(value: number): string | null {
  if (!Number.isFinite(value)) return null;
  if (value <= -30) return 'overload';
  if (value < -10) return 'optimal';
  if (value < 5) return 'maintaining';
  if (value < 30) return 'fresh';
  return 'recharging';
}
